/**
 * Renders the limited markdown used by the legal documents (headings, paragraphs,
 * lists, tables, bold, links, escaped characters, hard line breaks) as React elements.
 * Text is never injected as HTML; only platform legal pages, https and mailto links become links.
 */

import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

const unescape = (text: string) => text.replace(/\\([[\]()*_#|\\-])/g, '$1');
const token = /(\*\*[^*]+\*\*|(?<!\\)\[[^\]\\]+\]\([^)\s]+\))/g;

function piece(text: string, key: string): ReactNode {
  if (/^\*\*[^*]+\*\*$/.test(text)) return <strong key={key}>{unescape(text.slice(2, -2))}</strong>;
  const link = text.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
  if (!link) return <Fragment key={key}>{unescape(text)}</Fragment>;
  const [, label, href] = link as unknown as [string, string, string];
  if (/^\/legal\/[a-z-]+$/.test(href)) return <Link key={key} className="text-indigo-600 hover:text-indigo-800" to={href}>{unescape(label)}</Link>;
  if (/^(https:\/\/|mailto:)/i.test(href)) return <a key={key} className="text-indigo-600 hover:text-indigo-800" href={href} target={href.startsWith('mailto:') ? undefined : '_blank'} rel="noopener noreferrer">{unescape(label)}</a>;
  return <Fragment key={key}>{unescape(label)}</Fragment>;
}

function inline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const lines = text.split('\n');
  lines.forEach((raw, lineIndex) => {
    const hardBreak = raw.endsWith('\\');
    const line = hardBreak ? raw.slice(0, -1) : raw;
    line.split(token).forEach((text, i) => { if (text) parts.push(piece(text, `${lineIndex}-${i}`)); });
    if (lineIndex < lines.length - 1) parts.push(hardBreak ? <br key={`br-${lineIndex}`} /> : ' ');
  });
  return parts;
}

const cells = (row: string) => row.trim().replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());

export default function LegalMarkdown({ markdown }: { markdown: string }) {
  const blocks = markdown.split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);
  return (
    <div className="space-y-3 text-sm leading-relaxed text-gray-800">
      {blocks.map((block, index) => {
        const heading = block.match(/^(#{2,4}) (.+)$/);
        if (heading) {
          const level = heading[1]!.length;
          const className = level === 2 ? 'pt-3 text-lg font-semibold text-gray-900' : 'pt-2 text-base font-semibold text-gray-900';
          return level === 2 ? <h2 key={index} className={className}>{inline(heading[2]!)}</h2> : <h3 key={index} className={className}>{inline(heading[2]!)}</h3>;
        }
        const lines = block.split('\n');
        if (lines.every((line) => line.startsWith('- '))) {
          return <ul key={index} className="list-disc space-y-1 pl-6">{lines.map((line, i) => <li key={i}>{inline(line.slice(2))}</li>)}</ul>;
        }
        if (lines.length >= 2 && lines.every((line) => line.trim().startsWith('|')) && /^\|?\s*-{3,}/.test(lines[1]!.trim())) {
          const [head, , ...rows] = lines;
          return (
            <div key={index} className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-left text-sm">
                <thead className="bg-gray-50"><tr>{cells(head!).map((cell, i) => <th key={i} className="border-b px-3 py-2 font-semibold">{inline(cell)}</th>)}</tr></thead>
                <tbody>{rows.map((row, r) => <tr key={r}>{cells(row).map((cell, i) => <td key={i} className="border-b px-3 py-2">{inline(cell)}</td>)}</tr>)}</tbody>
              </table>
            </div>
          );
        }
        return <p key={index}>{inline(block)}</p>;
      })}
    </div>
  );
}
